import { ResourceType } from "@/game/entities/config/resources";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import {
	type ProductionValue,
	type TileConfig,
	type TileConfigItem,
	type UpgradeParams,
} from "@/types/tile";
import { ceilNumber } from "@/utils/number";
import {
	FrameName,
	tilerepoLandRoadsOnlyFrameNames,
	tilerepoRiverRoadsOnlyFrameNames,
} from ".";
import { getTerrainConfig } from "./terrain";
import { UpgradeIds } from "./upgrades";

/** CONFIG **/

const config: TileConfig = {
	[FrameName.DIRT_ROAD_TUTORIAL]: {
		requirements: new Map<ResourceType, number>([[ResourceType.LEVEL, 1]]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.DIRT_ROAD,
				{
					price: {
						[ResourceType.COINS]: 300,
					},
				},
			],
		]),
	},
	[FrameName.DIRT_ROAD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 1],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(300 / 4),
					},
				},
			],
			[
				FrameName.GRAVEL_ROAD,
				{
					price: {
						[ResourceType.COINS]: 700,
						[ResourceType.STONE]: 50,
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 4_700,
						[ResourceType.STONE]: 500,
					},
				},
			],
		]),
	},
	[FrameName.GRAVEL_ROAD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 11],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base =
						5 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.road_toll);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(1_000 / 4),
						[ResourceType.STONE]: ceilNumber(50 / 4),
					},
				},
			],
			[
				FrameName.COBBLED_ROAD,
				{
					price: {
						[ResourceType.COINS]: 4_000,
						[ResourceType.STONE]: 450,
					},
				},
			],
		]),
	},
	[FrameName.COBBLED_ROAD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 24],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base =
						15 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.road_toll2,
						);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(5_000 / 4),
						[ResourceType.STONE]: ceilNumber(500 / 4),
					},
				},
			],
		]),
	},
	[FrameName.FORD_CROSSING]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 1],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		production: new Map<ResourceType, number>(),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(500 / 4),
					},
				},
			],
			[
				FrameName.WOODEN_BRIDGE,
				{
					price: {
						[ResourceType.COINS]: 1_500,
						[ResourceType.STONE]: 500,
					},
				},
			],
			[
				FrameName.STONE_BRIDGE,
				{
					price: {
						[ResourceType.COINS]: 9_500,
						[ResourceType.STONE]: 2_500,
					},
				},
			],
		]),
	},
	[FrameName.WOODEN_BRIDGE]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 11],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base =
						5 +
						getUpgradeCurrentReplaceValueById(UpgradeIds.road_toll);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(2_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(500 / 4),
					},
				},
			],
			[
				FrameName.STONE_BRIDGE,
				{
					price: {
						[ResourceType.COINS]: 8_000,
						[ResourceType.STONE]: 2_000,
					},
				},
			],
		]),
	},
	[FrameName.STONE_BRIDGE]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 24],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base =
						15 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.road_toll2,
						);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(10_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(2_500 / 4),
					},
				},
			],
		]),
	},
	[FrameName.MOUNTAIN_ROAD]: {
		inSearchTool: true,
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 34],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base =
						15 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.road_toll2,
						);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(50_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(10_000 / 4),
						[ResourceType.IRON]: ceilNumber(1_000 / 4),
					},
				},
			],
		]),
	},
	[FrameName.HILLY_ROAD]: {
		requirements: new Map<ResourceType, number>([
			[ResourceType.LEVEL, 34],
			[ResourceType.SETTLEMENT_CONNECTION, 1],
		]),
		production: new Map<ResourceType, ProductionValue>([
			[
				ResourceType.COINS_INCOME,
				(): number => {
					const base =
						15 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.road_toll2,
						);
					const multiplier = 1;
					return ceilNumber(base * multiplier);
				},
			],
		]),
		consumption: new Map<ResourceType, number>(),
		storage: new Map<ResourceType, number>(),
		upgrades: new Map<FrameName, UpgradeParams>([
			[
				FrameName.NOT_SET,
				{
					reward: {
						[ResourceType.COINS]: ceilNumber(50_000 / 4),
						[ResourceType.LUMBER]: ceilNumber(10_000 / 4),
						[ResourceType.IRON]: ceilNumber(1_000 / 4),
					},
				},
			],
		]),
	},
};

const grassland = getTerrainConfig(FrameName.GRASSLAND);
const river = getTerrainConfig(FrameName.RIVER5);
const hills = getTerrainConfig(FrameName.HILLS);
const mountains = getTerrainConfig(FrameName.MOUNTAINS);

for (const frameName in config) {
	if (tilerepoLandRoadsOnlyFrameNames.has(frameName as FrameName)) {
		for (const grassUpgrade of grassland.upgrades.entries()) {
			if (frameName === grassUpgrade[0]) continue;
			if (tilerepoLandRoadsOnlyFrameNames.has(grassUpgrade[0])) continue;
			if (config[frameName as FrameName]?.upgrades.has(grassUpgrade[0]))
				continue;
			if (
				[
					FrameName.DIRT_ROAD_TUTORIAL,
					FrameName.LIGHTHOUSE,
					FrameName.SMALL_HOUSES,
					FrameName.FOREST,
					FrameName.DENSE_FOREST,
				].includes(grassUpgrade[0])
			)
				continue;

			const selfReward =
				config[frameName as FrameName]?.upgrades.get(FrameName.NOT_SET)
					?.reward ?? undefined; // tu musi byt undefined, aby sa ten kluc v objekte neparsoval, nemozme tam dat null

			config[frameName as FrameName]?.upgrades.set(grassUpgrade[0], {
				...grassUpgrade[1],
				reward: selfReward,
			});
		}
	}

	if (tilerepoRiverRoadsOnlyFrameNames.has(frameName as FrameName)) {
		for (const riverUpgrade of river.upgrades.entries()) {
			if (frameName === riverUpgrade[0]) continue;
			if (tilerepoRiverRoadsOnlyFrameNames.has(riverUpgrade[0])) continue;
			if (config[frameName as FrameName]?.upgrades.has(riverUpgrade[0]))
				continue;

			const selfReward =
				config[frameName as FrameName]?.upgrades.get(FrameName.NOT_SET)
					?.reward ?? undefined; // tu musi byt undefined, aby sa ten kluc v objekte neparsoval, nemozme tam dat null

			config[frameName as FrameName]?.upgrades.set(riverUpgrade[0], {
				...riverUpgrade[1],
				reward: selfReward,
			});
		}
	}

	if (frameName === FrameName.HILLY_ROAD) {
		for (const hillsUpgrade of hills.upgrades.entries()) {
			if (frameName === hillsUpgrade[0]) continue;
			if (config[frameName as FrameName]?.upgrades.has(hillsUpgrade[0]))
				continue;

			const selfReward =
				config[frameName as FrameName]?.upgrades.get(FrameName.NOT_SET)
					?.reward ?? undefined; // tu musi byt undefined, aby sa ten kluc v objekte neparsoval, nemozme tam dat null

			config[frameName as FrameName]?.upgrades.set(hillsUpgrade[0], {
				...hillsUpgrade[1],
				reward: selfReward,
			});
		}
	}

	if (frameName === FrameName.MOUNTAIN_ROAD) {
		for (const mountainsUpgrade of mountains.upgrades.entries()) {
			if (frameName === mountainsUpgrade[0]) continue;
			if (
				config[frameName as FrameName]?.upgrades.has(
					mountainsUpgrade[0],
				)
			)
				continue;

			const selfReward =
				config[frameName as FrameName]?.upgrades.get(FrameName.NOT_SET)
					?.reward ?? undefined; // tu musi byt undefined, aby sa ten kluc v objekte neparsoval, nemozme tam dat null

			config[frameName as FrameName]?.upgrades.set(mountainsUpgrade[0], {
				...mountainsUpgrade[1],
				reward: selfReward,
			});
		}
	}
}

const configWithoutDuplicates: TileConfig = { ...config };

config[FrameName.DIRT_ROAD_1] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_2] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_3] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_4] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_5] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_6] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_7] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_8] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_9] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_10] = Object.assign({}, config[FrameName.DIRT_ROAD]);
config[FrameName.DIRT_ROAD_11] = Object.assign({}, config[FrameName.DIRT_ROAD]);

config[FrameName.GRAVEL_ROAD_1] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_2] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_3] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_4] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_5] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_6] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_7] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_8] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_9] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_10] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);
config[FrameName.GRAVEL_ROAD_11] = Object.assign(
	{},
	config[FrameName.GRAVEL_ROAD],
);

config[FrameName.COBBLED_ROAD_1] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_2] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_3] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_4] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_5] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_6] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_7] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_8] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_9] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_10] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);
config[FrameName.COBBLED_ROAD_11] = Object.assign(
	{},
	config[FrameName.COBBLED_ROAD],
);

config[FrameName.FORD_CROSSING_1] = Object.assign(
	{},
	config[FrameName.FORD_CROSSING],
);
config[FrameName.FORD_CROSSING_2] = Object.assign(
	{},
	config[FrameName.FORD_CROSSING],
);

config[FrameName.WOODEN_BRIDGE_1] = Object.assign(
	{},
	config[FrameName.WOODEN_BRIDGE],
);
config[FrameName.WOODEN_BRIDGE_2] = Object.assign(
	{},
	config[FrameName.WOODEN_BRIDGE],
);

config[FrameName.STONE_BRIDGE_1] = Object.assign(
	{},
	config[FrameName.STONE_BRIDGE],
);
config[FrameName.STONE_BRIDGE_2] = Object.assign(
	{},
	config[FrameName.STONE_BRIDGE],
);

export const getNewRoadsFromLevel = (level: number): Array<FrameName> => {
	const newRoads: Array<FrameName> = [];
	for (const [frameName, roadsConfig] of Object.entries(
		configWithoutDuplicates,
	)) {
		if (
			roadsConfig.requirements.has(ResourceType.LEVEL) &&
			roadsConfig.requirements.get(ResourceType.LEVEL)! === level
		) {
			newRoads.push(frameName as FrameName);
		}
	}
	return newRoads;
};

export const getRoadConfig = (frameName: FrameName): TileConfigItem => {
	const roadConfig = config?.[frameName];
	if (!roadConfig)
		throw new Error(`Road config not found for type: ${frameName}!`);

	return roadConfig;
};

export const isRoad = (frameName: FrameName): boolean => frameName in config;

export const getAllRoadsForSearchTool = (): Array<FrameName> => {
	const roads: Array<FrameName> = [];
	for (const [frameName, roadConfig] of Object.entries(
		configWithoutDuplicates,
	)) {
		if (roadConfig.inSearchTool) {
			roads.push(frameName as FrameName);
		}
	}
	return roads;
};
